import { useQuery } from '@apollo/client';
import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AppContextDocument } from './';
import { resolveTargetNetwork } from './target-network';

export const useFetchAppContext = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { data, loading } = useQuery(AppContextDocument, {
    fetchPolicy: 'cache-first',
    pollInterval: 30_000,
  });

  const search = useParams<{ networkId: string }>();

  const {
    announcementCount,
    currentNetwork,
    currentUser,
    env,
    impersonating,
    trueUser,
    unreadAnnouncementCount,
  } = useMemo(() => {
    const networkId = Number(search.networkId);
    return {
      announcementCount: data?.announcements.pageInfo.nodesCount ?? 0,
      currentUser: data?.currentUser,
      currentNetwork: data?.currentUser?.networks.find((x) => x.id === networkId),
      env: data?.env ?? 'development',
      impersonating: data?.currentUser && data.trueUser && data.currentUser.id !== data.trueUser.id,
      trueUser: data?.trueUser,
      unreadAnnouncementCount: data?.currentUser?.unreadAnnouncementCount ?? 0,
    };
  }, [
    data?.announcements.pageInfo.nodesCount,
    data?.currentUser,
    data?.env,
    data?.trueUser,
    search.networkId,
  ]);

  // handle user not logged in
  useEffect(() => {
    if (loading || currentUser) return;
    navigate('/login', { replace: true, state: { from: location } });
  }, [currentUser, loading, location, navigate]);

  // handle top level redirect, or bad network id
  useEffect(() => {
    if (loading || currentNetwork || !currentUser) return;
    const targetNetwork = resolveTargetNetwork(currentUser);
    navigate(`/networks/${targetNetwork?.id}${location.pathname}`);
  }, [currentNetwork, currentUser, currentUser?.networks, loading, location.pathname, navigate]);

  return {
    announcementCount,
    currentNetwork,
    currentUser,
    env,
    impersonating,
    trueUser,
    unreadAnnouncementCount,
  };
};
